import is from '@sindresorhus/is';
import type { ReactNode } from 'react';
import { memo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useHasRole } from '~/shared/context/use-has-role';
import { useTranslate } from '~/shared/context/use-translate';
import type { JobArchitecture, JobArchitectureItemStatistics } from '~/shared/data-contract/job-architecture';
import type { JobArchitectureStatistics } from '~/ui/domain/job-architecture-select/JobArchitectureSelect';
import { JobArchitectureSelect as JobArchitectureSelectComponent } from '~/ui/domain/job-architecture-select/JobArchitectureSelect';

import { useCreateJobArchitecture } from '../api/use-create-job-architecture';
import { useJobArchitecture } from '../api/use-job-architecture';
import { useJobArchitectures } from '../api/use-job-architectures';
import { useSetupEntryPath } from '../setup-dialog/use-setup-entry-path';
import { ArchitectureStatsRow } from './ArchitectureStatsRow';

export const JobArchitectureSelect = memo(
    ({
        activeJobArchitectureId,
        hideStatistics,
        onSwitchArchitecture,
        size,
        triggerBackgroundColor,
    }: {
        readonly activeJobArchitectureId?: string;
        readonly hideStatistics?: boolean;
        readonly onSwitchArchitecture: (architecture: JobArchitecture) => void;
        readonly size?: 'l' | 'm';
        readonly triggerBackgroundColor?: string;
    }) => {
        const { data: architectures } = useJobArchitectures();
        const { data: currentArchitectureDetails } = useJobArchitecture(activeJobArchitectureId);
        const translate = useTranslate();
        const isJobArchitect = useHasRole('job_architect');

        const [isOpen, setIsOpen] = useState(false);
        const navigate = useNavigate();

        const { mutateAsync: createJobArchitecture } = useCreateJobArchitecture();
        const buildSetupEntryPath = useSetupEntryPath();

        const getStatsContent = (data: JobArchitectureStatistics | undefined): ReactNode => {
            if (data === undefined || hideStatistics === true) return null;

            const hasHierarchyStats =
                typeof data.familiesCount === 'number' &&
                typeof data.clustersCount === 'number' &&
                typeof data.rolesCount === 'number' &&
                typeof data.levelsCount === 'number';

            if (hasHierarchyStats) {
                return <ArchitectureStatsRow isActive={false} statistics={data as JobArchitectureItemStatistics} />;
            }

            return null;
        };

        const currentArchitecture = is.undefined(currentArchitectureDetails)
            ? architectures?.find((architecture) => architecture.id === activeJobArchitectureId)
            : currentArchitectureDetails;

        const handleCreateNewArchitecture = async (): Promise<void> => {
            const newArchitecture = await createJobArchitecture();
            setIsOpen(false);
            navigate(buildSetupEntryPath(newArchitecture.id));
        };

        return (
            <JobArchitectureSelectComponent
                architectures={architectures ?? []}
                createNewArchitecture={translate('pages.job-architecture.new-job-architecture')}
                currentArchitecture={currentArchitecture}
                data-testid="job-architecture-select"
                getStatsContent={getStatsContent}
                open={isOpen}
                placeholder={translate('pages.job-architecture-v2.select-architecture.placeholder')}
                size={size}
                triggerBackgroundColor={triggerBackgroundColor}
                onCreateNewArchitecture={isJobArchitect ? handleCreateNewArchitecture : undefined}
                onOpenChange={setIsOpen}
                onSwitchArchitecture={(id) => {
                    const architecture = architectures?.find((arch) => arch.id === id);
                    if (is.undefined(architecture)) {
                        return;
                    }

                    onSwitchArchitecture(architecture);
                }}
            />
        );
    },
);
